/**
 * CategoryFilter — Sprint 04-30 P2
 *
 * Multi-select for procurementCategory. Same popover UX as DateRangeFilter
 * so the filter row stays visually consistent.
 *
 * The pure "Services" value is intentionally NOT a checkbox — Liam's P0
 * (Sprint 04-30) keeps that hard-excluded at the search-service level. If a
 * doc somehow gets through with combo values like "Goods, Services", the
 * filter still works on it because the combo is a distinct value in ES.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tag } from 'lucide-react';

const DEFAULT_OPTIONS = [
  { value: 'Goods',                     label: 'Goods' },
  { value: 'Construction',              label: 'Construction' },
  { value: 'Services related to goods', label: 'Services related to goods' },
  { value: 'Goods, Services',           label: 'Goods + Services' },
  { value: 'Construction, Goods',       label: 'Construction + Goods' },
];

const CategoryFilter = ({ selected, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected || []);
  const popRef = useRef(null);

  // Re-sync when caller's selection changes (URL-driven)
  useEffect(() => { setDraft(selected || []); }, [selected]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeCount = (selected && selected.length) || 0;

  const toggle = (val) => {
    setDraft((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const apply = () => { onApply(draft); setOpen(false); };
  const clear = () => { setDraft([]); onApply([]); setOpen(false); };

  const buttonLabel = useMemo(() => {
    if (activeCount === 0) return 'All categories';
    if (activeCount === 1) return selected[0];
    return `${activeCount} selected`;
  }, [activeCount, selected]);

  return (
    <div className="cat-filter-wrap" ref={popRef}>
      <button
        type="button"
        className={`cat-filter-btn ${activeCount > 0 ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="Filter by procurement category"
      >
        <Tag size={14} />
        <span className="cat-filter-btn-label">{buttonLabel}</span>
      </button>

      {open && (
        <div className="cat-filter-popover" role="dialog">
          <div className="cat-filter-list">
            {DEFAULT_OPTIONS.map((opt) => (
              <label key={opt.value} className="cat-filter-row">
                <input
                  type="checkbox"
                  checked={draft.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          <div className="cat-filter-actions">
            <button type="button" className="cat-filter-link" onClick={clear}>
              Clear
            </button>
            <button type="button" className="cat-filter-apply" onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryFilter;
